import { setUserId } from '@/common/utils/localUserId'
import { backToSignIn } from '@/common/utils/login'
import {
  SUBDOMAIN_COOKIE_KEY,
  getRootDomainFromUrl,
  getSubDomainFromCurrentUrl,
  navigatingOnAvailableSubdomain,
  urlPathJoin,
} from '@/common/utils/url'
import { Login } from '@/models/login.model'
import { ROOMS_ROUTE_NAME } from '@/router/routerData'
import { getRouter } from '@/router/routerInstance'
import loginService from '@/services/login.service'
import {
  ACTIVATE_ACCOUNT,
  CHECK_TOKEN,
  FORGOT_PASSWORD,
  GET_AUTH_FROM_TOKEN,
  RESEND_TWO_FACTORS_CODE,
  RESET_PASSWORD,
  SAML_CHECK,
  SIGN_IN,
  VALIDATE_TWO_FACTORS_CODE,
} from '@/store/modules/login/action_types'

import {
  SET_AUTH_FROM_TOKEN_ERROR,
  SET_AUTH_FROM_TOKEN_PENDING,
  SET_LOADING_PAGE,
  SET_SIGNIN_PENDING,
  SET_USER_TOKEN_SCOPE,
} from './mutation_types'
import { SET_USER_PROFILE } from '../user/mutation_types'

/**
 * Sets up the store and storage when a new user gets logged in
 * 
 * SECURITY: Tokens are NO LONGER persisted to localStorage.
 * The backend MUST set the token in an HTTP-only cookie with Secure and SameSite flags.
 * This function only:
 * 1. Sets the user ID in localStorage (non-sensitive, used for session detection)
 * 2. Commits user profile to store (triggers App.vue to initialize)
 * 
 * @param {object} data The data received from a successful login endpoint
 * @param {function} commit The module-bound commit function from a login action
 */
function setNewUser (data, commit) {
  // Store user ID only (non-sensitive, used to detect multi-tab logout)
  setUserId(data.user.id)
  
  commit(SET_USER_TOKEN_SCOPE, data.scopes)

  // Commit user profile - this triggers App.vue watcher to initialize
  // (no need to check localStorage token anymore)
  commit('user/' + SET_USER_PROFILE, data.user, { root: true })
}

export const actions = {
  async [GET_AUTH_FROM_TOKEN] ({ commit }, { params, withoutApiToken = false }) {
    commit(SET_AUTH_FROM_TOKEN_PENDING, true)
    try {
      return loginService.getAuthFromToken(params, withoutApiToken)
    } catch (error) {
      commit(SET_AUTH_FROM_TOKEN_ERROR, error)
      throw error
    } finally {
      commit(SET_AUTH_FROM_TOKEN_PENDING, false)
    }
  },
  /**
  * @function Saml checking
  * @param {store} store
  * @param {object} queryParams
  */
  async [SAML_CHECK] ({ commit }, queryParams) {
    commit('SET_LOADING', true)
    try {
      const data = await loginService.samlCheck(queryParams)
      if (data.data.samlEnabled && data.data.redirTo) {
        commit(SET_SIGNIN_PENDING, true)
        window.location = data.data.redirTo
      }
    } catch (error) {
      commit('SET_SAMLCHECK_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  /**
     * @function Login function
     * @param {store} store
     * @param {Login} login
     * @param {string} redirectRoute
     * @param {string} redirectUrl
     */
  async [SIGN_IN] ({ commit, rootGetters }, { login, redirectRoute, redirectUrl }) {
    if (login instanceof Login) {
      commit('SET_LOADING', true)

      try {
        const { data } = await loginService.signIn(login)
        const signingFromAvailableSubdomain = navigatingOnAvailableSubdomain()
        if (signingFromAvailableSubdomain) {
          const cookieDomain = getRootDomainFromUrl(import.meta.env.VUE_APP_CLIENT_URL)
          window.$cookies.set(SUBDOMAIN_COOKIE_KEY, getSubDomainFromCurrentUrl(), 0, null, cookieDomain)
        } else {
          setNewUser(data, commit)

          if (rootGetters['user/customizationEnabled']) {
            rebootApp()
          }
        }
        let targetRedirectUrl = redirectUrl
        if (signingFromAvailableSubdomain && !targetRedirectUrl) {
          const targetPath = redirectRoute
            ? (typeof redirectRoute === 'string' ? redirectRoute : getRouter().resolve(redirectRoute).href)
            : '/'
          targetRedirectUrl = urlPathJoin(import.meta.env.VUE_APP_CLIENT_URL, targetPath)
        }

        if (targetRedirectUrl) {
          window.location = targetRedirectUrl
        } else if (redirectRoute) {
          getRouter().push(redirectRoute)
        } else {
          getRouter().push({ name: ROOMS_ROUTE_NAME })
        }
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 422) {
          commit('SET_SIGNIN_ERROR', error.response.statusText)
          throw error
        }
      } finally {
        commit('SET_LOADING', false)
      }
    }
  },
  /**
     * @function Login function
     * @param {store} store
     * @param {Login} login
     */
  async [FORGOT_PASSWORD] ({ commit }, login) {
    if (login instanceof Login) {
      commit('SET_LOADING', true)
      return new Promise((resolve, reject) => {
        loginService.forgotPassword(login)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            commit('SET_FORGOT_PASSWORD_ERROR', error)
            reject(error)
            throw error
          })
          .then(() => {
            commit('SET_LOADING', false)
          })
      })
    }
  },
  /**
     * @function Login function
     * @param {store} store
     * @param {Login} login
     */
  async [RESET_PASSWORD] ({ commit, rootGetters }, login) {
    if (login instanceof Login) {
      commit('SET_LOADING', true)
      try {
        const { data } = await loginService.resetPassword(login)

        const resettingPasswordFromAvailableSubdomain = navigatingOnAvailableSubdomain()
        let targetRedirectUrl = null
        if (resettingPasswordFromAvailableSubdomain) {
          const cookieDomain = getRootDomainFromUrl(import.meta.env.VUE_APP_CLIENT_URL)
          window.$cookies.set(SUBDOMAIN_COOKIE_KEY, getSubDomainFromCurrentUrl(), 0, null, cookieDomain)
          targetRedirectUrl = urlPathJoin(import.meta.env.VUE_APP_CLIENT_URL, '/')
        } else {
          setNewUser(data, commit)
          if (rootGetters['user/customizationEnabled']) {
            rebootApp()
          }
        }
        if (targetRedirectUrl) {
          window.location = targetRedirectUrl
        } else {
          getRouter().push({ name: ROOMS_ROUTE_NAME })
        }
      } catch (error) {
        commit('SET_FORGOT_PASSWORD_ERROR', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    }
  },
  /**
     * @function Login function
     * @param {store} store
     * @param {Login} login
     * @param {object} redirectRoute A route to go to if successful. Undefined if default should be used
     */
  async [ACTIVATE_ACCOUNT] ({ commit, rootGetters }, { login, redirectRoute }) {
    if (login instanceof Login) {
      commit('SET_LOADING', true)
      try {
        const { data } = await loginService.activateAccount(login)

        const activatingFromAvailableSubdomain = navigatingOnAvailableSubdomain()
        let targetRedirectUrl = null
        if (activatingFromAvailableSubdomain) {
          const cookieDomain = getRootDomainFromUrl(import.meta.env.VUE_APP_CLIENT_URL)
          window.$cookies.set(SUBDOMAIN_COOKIE_KEY, getSubDomainFromCurrentUrl(), 0, null, cookieDomain)
          const targetPath = redirectRoute ? getRouter().resolve(redirectRoute).href : '/'
          targetRedirectUrl = urlPathJoin(import.meta.env.VUE_APP_CLIENT_URL, targetPath)
        } else {
          setNewUser(data, commit)
          if (rootGetters['user/customizationEnabled']) {
            rebootApp()
          }
        }

        if (targetRedirectUrl) {
          window.location = targetRedirectUrl
        } else if (redirectRoute) {
          getRouter().push(redirectRoute)
        } else {
          getRouter().push({ name: ROOMS_ROUTE_NAME })
        }
      } catch (error) {
        commit('SET_FORGOT_PASSWORD_ERROR', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    }
  },
  /**
     * @function Login Check token validity
     * @param {store} store
     * @param {String} token
     */
  async [CHECK_TOKEN] ({ commit }, token) {
    try {
      await loginService.checkToken(token)
      commit(SET_LOADING_PAGE, false)
    } catch (error) {
      backToSignIn({ isTokenInvalid: true })
    }
  },

  /**
     * @function Login Send code to valid 2FA
     * @param {store} store
     * @param {Object} {code}
     */
  async [VALIDATE_TWO_FACTORS_CODE] ({ commit }, { code }) {
    commit('SET_LOADING', true)
    try {
      const { data } = await loginService.sendTwoFactorCode(code)
      return data
    } catch (error) {
      commit('SET_TWO_FACTOR_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
     * @function Login resend code to valid 2FA
     * @param {store} store
     */
  async [RESEND_TWO_FACTORS_CODE] ({ commit }) {
    commit('SET_LOADING', true)
    try {
      await loginService.resendTwoFactorCode()
    } catch (error) {
      commit('SET_TWO_FACTOR_ERROR', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
}

function rebootApp () {
  // Boot the app again to correctly reset Vuetify themes
  window.location.replace(
    getRouter().resolve({ name: ROOMS_ROUTE_NAME }).href,
  )
}
